import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useProfileInfo } from "../contexts/ProfileInfoContext";
import { useNotifications } from "../hooks/useNotifications.js";
import useCVAnalysis from "../hooks/useCVAnalysis";
import NotificationDropdown from "./NotificationDropdown.jsx";

// Image - using userIcon.png from public directory

const Navbar = ({ onToggleSidebar, sidebarOpen, isMobile }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const userMenuRef = useRef(null);
    // notifications API (shared with dropdown so badges sync after markAsRead)
    const notificationsApi = useNotifications();
    const { unreadMessages } = notificationsApi;
    const { user, signOut } = useAuth();
    const { profileInfo } = useProfileInfo();
    const { getLatestAnalysis } = useCVAnalysis(user);
    const [isMobileMessagesOpen, setIsMobileMessagesOpen] = useState(false);
    const [isDesktopMessagesOpen, setIsDesktopMessagesOpen] = useState(false);
    const hasUnreadMessages = unreadMessages?.length > 0;
    const marketingNavPaths = ["/employer-landing-page", "/employee-landing-page", "/blogs"];
    const showMarketingLinks = marketingNavPaths.includes(location.pathname);
    const isLandingPage = location.pathname === "/";
    const shouldShowLogo = !user || isLandingPage || showMarketingLinks;

    // Determine if Dashboard menu item should be shown (on home, login, or signup pages)
    const shouldShowDashboard = user && (location.pathname === '/' || location.pathname === '/login' || location.pathname === '/signup');

    // Helper function to get dashboard path based on user role
    const getDashboardPath = () => {
        const isEmployerPath = location.pathname.startsWith('/employer/');
        const isEmployeePath = location.pathname.startsWith('/employee/');

        if (isEmployerPath) {
            return '/employer/dashboard';
        } else if (isEmployeePath || profileInfo?.role === 'Employee') {
            return '/employee/dashboard';
        } else {
            // Default to employer if role is not clearly defined
            return '/employer/dashboard';
        }
    };

    // Close user menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleCVAnalyzerClick = async (e) => {
        e.preventDefault();
        if (user) {
            const latestAnalysis = await getLatestAnalysis();
            if (latestAnalysis && latestAnalysis.cv_analysis_results) {
                navigate(`/cv-analysis-results?id=${latestAnalysis.id}`);
            } else {
                navigate("/cv-analyser");
            }
        } else {
            navigate("/cv-analyser");
        }
    };

    // Determine avatar image (company logo for employers if uploaded)
    const isEmployerPath = location.pathname.startsWith('/employer/');
    const isEmployeePath = location.pathname.startsWith('/employee/');
    const isEmployerUser = isEmployerPath || profileInfo?.role === 'Employer';
    const employerLogoUrl = user?.user_metadata?.company_logo_url
        || user?.user_metadata?.employer_profile?.companyInfo?.logoUrl
        || '';
    const employeeAvatarUrl = user?.user_metadata?.profile_image_url || '';
    const avatarImageSrc = (isEmployerUser && employerLogoUrl) ? employerLogoUrl : (employeeAvatarUrl || '/userIcon.png');

    return (
        <>
            <nav
                className={`sticky top-0 z-30 ${["/"].includes(location.pathname)
                    ? "bg-[#f9f9f9]"
                    : "bg-white"
                    }`}
            >
                <div className="max-w-full mx-auto px-6 py-2">
                    <div className="flex justify-between items-center h-10">
                        <div className="flex items-center gap-3">
                            {/* Mobile Hamburger Menu Button - Only for logged-in users */}
                            {user && (
                                <button
                                    className="md:hidden p-2 mr-2"
                                    onClick={() => onToggleSidebar && onToggleSidebar()}
                                >
                                    {isMobile && sidebarOpen ? (
                                        // Cross icon when sidebar is open
                                        <div className="w-6 h-6 flex items-center justify-center">
                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </div>
                                    ) : (
                                        // Hamburger icon when sidebar is closed
                                        <div className="w-6 h-5 flex flex-col justify-between">
                                            <span className="w-full h-0.5 bg-black"></span>
                                            <span className="w-full h-0.5 bg-black"></span>
                                            <span className="w-full h-0.5 bg-black"></span>
                                        </div>
                                    )}
                                </button>
                            )}

                            {/* Logo - keep visible on landing page even when logged in */}
                            {shouldShowLogo && (
                                <div
                                    className="logo cursor-pointer"
                                    onClick={() => navigate("/")}
                                >
                                    Doctors' Jobs <span className="logo-badge">Beta</span>
                                </div>
                            )}
                        </div>

                        {/* Marketing navigation links - show for marketing pages or landing page */}
                        {(showMarketingLinks || isLandingPage || !user) && (
                            <nav className="hidden md:flex flex-1 justify-center items-center space-x-6">
                                <Link
                                    to="/employer-landing-page"
                                    className={`text-sm ${location.pathname === '/employer-landing-page' ? 'font-semibold' : 'font-normal'} text-gray-900 hover:text-gray-900`}
                                >
                                    Hire a doctor
                                </Link>
                                <Link
                                    to="/employee-landing-page"
                                    className={`text-sm ${location.pathname === '/employee-landing-page' ? 'font-semibold' : 'font-normal'} text-gray-900 hover:text-gray-900`}
                                >
                                    Find a role
                                </Link>
                                {!user && (
                                    <Link
                                        to="/#mock-interview"
                                        className={`text-sm ${location.hash === '#mock-interview' ? 'font-semibold' : 'font-normal'} text-gray-900 hover:text-gray-900`}
                                        onClick={(e) => {
                                            if (location.pathname === '/') {
                                                e.preventDefault();
                                                const element = document.getElementById('mock-interview');
                                                if (element) {
                                                    const navbarHeight = 80;
                                                    const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                                                    const offsetPosition = elementPosition - navbarHeight;
                                                    window.scrollTo({
                                                        top: offsetPosition,
                                                        behavior: 'smooth'
                                                    });
                                                    // Update URL hash without triggering navigation
                                                    window.history.pushState(null, '', '#mock-interview');
                                                }
                                            }
                                        }}
                                    >
                                        Mock Interview
                                    </Link>
                                )}
                                <Link
                                    to="/blogs"
                                    className={`text-sm ${location.pathname === '/blogs' ? 'font-semibold' : 'font-normal'} text-gray-900 hover:text-gray-900`}
                                >
                                    Blog
                                </Link>
                            </nav>
                        )}

                        {/* Desktop Navigation for logged-in users - HIDDEN since we have sidebar navigation */}
                        {/* {user && (
                        <div className="hidden md:flex flex-1 justify-center">
                            <div className="flex space-x-8">
                                <Link
                                    to="/"
                                    className={`text-lg font-medium text-black hover:underline decoration-2 ${
                                        location.pathname === "/" && "underline"
                                    }`}
                                >
                                    Job Board
                                </Link>
                                <Link
                                    to="/job-applications-tracker"
                                    className={`text-lg font-medium text-black hover:underline decoration-2 ${
                                        location.pathname ===
                                            "/job-applications-tracker" &&
                                        "underline"
                                    }`}
                                >
                                    Job Applications Tracker
                                </Link>
                                <a
                                    href="#"
                                    onClick={handleCVAnalyzerClick}
                                    className={`text-lg font-medium text-black hover:underline decoration-2 ${
                                        (location.pathname === "/cv-analyser" ||
                                        location.pathname === "/cv-analysis-results") &&
                                        "underline"
                                    }`}
                                >
                                    CV Analyser
                                </a>
                            </div>
                        </div>
                    )} */}

                        <div className="flex items-center space-x-3">
                            {/* Mobile Auth Buttons */}
                            {user ? (
                                <div className="flex md:hidden items-center space-x-2 relative">
                                    {/* Messages Icon */}
                                    <button
                                        className="rounded-full hover:bg-gray-100 transition p-2 relative"
                                        aria-label="Messages"
                                        onClick={() => setIsMobileMessagesOpen((prev) => !prev)}
                                    >
                                        {/* Envelope icon */}
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="w-6 h-6"
                                            aria-hidden="true"
                                        >
                                            {/* Sleeker envelope (outline) */}
                                            <path d="M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
                                            <path d="M3 7l9 6 9-6" />
                                        </svg>

                                        {hasUnreadMessages && (
                                            <span className="absolute top-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></span>
                                        )}
                                    </button>
                                    <NotificationDropdown
                                        isOpen={isMobileMessagesOpen}
                                        onClose={() => setIsMobileMessagesOpen(false)}
                                        isEmployer={isEmployerUser}
                                        notificationsApi={notificationsApi}
                                    />
                                    {/* Profile Icon with Dropdown */}
                                    <div className="relative" ref={userMenuRef}>
                                        <button
                                            onClick={() => setShowUserMenu(!showUserMenu)}
                                            className="rounded-full transition p-2"
                                        >
                                            <img src={avatarImageSrc} alt="Profile" className="w-5 h-5 rounded-full object-cover" />
                                        </button>

                                        {/* Dropdown Menu */}
                                        {showUserMenu && (
                                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                                                {shouldShowDashboard && (
                                                    <button
                                                        className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-t-lg transition"
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setShowUserMenu(false);
                                                            navigate(getDashboardPath());
                                                        }}
                                                    >
                                                        Dashboard
                                                    </button>
                                                )}
                                                {profileInfo?.role === 'Employee' ? (
                                                    <button
                                                        className={`w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 transition ${shouldShowDashboard ? '' : 'rounded-t-lg'}`}
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setShowUserMenu(false);
                                                            navigate("/employee/my-profile");
                                                        }}
                                                    >
                                                        My profile
                                                    </button>
                                                ) : (
                                                    <button
                                                        className={`w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 transition ${shouldShowDashboard ? '' : 'rounded-t-lg'}`}
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setShowUserMenu(false);
                                                            navigate("/employer/my-profile");
                                                        }}
                                                    >
                                                        My profile
                                                    </button>
                                                )}
                                                <button
                                                    className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 transition"
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setShowUserMenu(false);
                                                        // Use current path to determine which account settings to navigate to
                                                        const isOnEmployerRoute = location.pathname.startsWith('/employer');
                                                        const accountSettingsPath = isOnEmployerRoute ? '/employer/account-settings' : '/employee/account-settings';
                                                        navigate(accountSettingsPath);
                                                    }}
                                                >
                                                    Account settings
                                                </button>
                                                <button
                                                    className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 transition"
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setShowUserMenu(false);
                                                        window.open("https://tally.so/r/wb6krg", "_blank");
                                                    }}
                                                >
                                                    Support
                                                </button>
                                                <button
                                                    className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-b-lg transition"
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setShowUserMenu(false);
                                                        signOut();
                                                    }}
                                                >
                                                    Logout
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                /* Mobile Hamburger Menu Button */
                                <div className="flex md:hidden items-center">
                                    <button
                                        className="p-2"
                                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                                    >
                                        <div className="w-6 h-5 flex flex-col justify-between">
                                            <span className={`w-full h-0.5 bg-black transform transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
                                            <span className={`w-full h-0.5 bg-black transition-all duration-300 ${isMenuOpen ? 'opacity-0' : ''}`}></span>
                                            <span className={`w-full h-0.5 bg-black transform transition-all duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
                                        </div>
                                    </button>
                                </div>
                            )}

                            {/* Desktop Auth Buttons */}
                            <div className="hidden md:flex items-center space-x-3">
                                {user ? (
                                    <>
                                        {/* Messages Icon - placed to the left of Notifications */}
                                        <div className="relative">
                                            <button
                                                className="rounded-full hover:bg-gray-100 transition p-2 relative"
                                                aria-label="Messages"
                                                onClick={() => setIsDesktopMessagesOpen((prev) => !prev)}
                                            >
                                                {/* Envelope icon */}
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1.2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    className="w-7 h-7"
                                                    aria-hidden="true"
                                                >
                                                    {/* Sleeker envelope (outline) */}
                                                    <path d="M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
                                                    <path d="M3 7l9 6 9-6" />
                                                </svg>

                                                {hasUnreadMessages && (
                                                    <span className="absolute top-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></span>
                                                )}
                                            </button>

                                            <NotificationDropdown
                                                isOpen={isDesktopMessagesOpen}
                                                onClose={() => setIsDesktopMessagesOpen(false)}
                                                isEmployer={isEmployerUser}
                                                notificationsApi={notificationsApi}
                                            />
                                        </div>

                                        {/* Profile Icon with Dropdown */}
                                        <div className="relative" ref={userMenuRef}>
                                            <button
                                                onClick={() => setShowUserMenu(!showUserMenu)}
                                                className="rounded-full transition p-2"
                                            >
                                                <img src={avatarImageSrc} alt="Profile" className="w-7 h-7 rounded-full object-cover" />
                                            </button>

                                            {/* Dropdown Menu */}
                                            {showUserMenu && (
                                                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                                                    {shouldShowDashboard && (
                                                        <button
                                                            className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-t-lg transition"
                                                            onClick={() => {
                                                                setShowUserMenu(false);
                                                                navigate(getDashboardPath());
                                                            }}
                                                        >
                                                            Dashboard
                                                        </button>
                                                    )}
                                                    {(() => {
                                                        // Determine user role - check current path first, then profileInfo
                                                        const isEmployerPath = location.pathname.startsWith('/employer/');
                                                        const isEmployeePath = location.pathname.startsWith('/employee/');

                                                        if (isEmployerPath) {
                                                            return (
                                                                <button
                                                                    className={`w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 transition ${shouldShowDashboard ? '' : 'rounded-t-lg'}`}
                                                                    onClick={() => {
                                                                        setShowUserMenu(false);
                                                                        navigate("/employer/my-profile");
                                                                    }}
                                                                >
                                                                    My profile
                                                                </button>
                                                            );
                                                        } else if (isEmployeePath || profileInfo?.role === 'Employee') {
                                                            return (
                                                                <button
                                                                    className={`w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 transition ${shouldShowDashboard ? '' : 'rounded-t-lg'}`}
                                                                    onClick={() => {
                                                                        setShowUserMenu(false);
                                                                        navigate("/employee/my-profile");
                                                                    }}
                                                                >
                                                                    My profile
                                                                </button>
                                                            );
                                                        } else {
                                                            // Default to employer if role is not clearly defined
                                                            return (
                                                                <button
                                                                    className={`w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 transition ${shouldShowDashboard ? '' : 'rounded-t-lg'}`}
                                                                    onClick={() => {
                                                                        setShowUserMenu(false);
                                                                        navigate("/employer/my-profile");
                                                                    }}
                                                                >
                                                                    My profile
                                                                </button>
                                                            );
                                                        }
                                                    })()}
                                                    <button
                                                        className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 transition"
                                                        onClick={() => {
                                                            setShowUserMenu(false);
                                                            // Use current path to determine which account settings to navigate to
                                                            const isOnEmployerRoute = location.pathname.startsWith('/employer');
                                                            const accountSettingsPath = isOnEmployerRoute ? '/employer/account-settings' : '/employee/account-settings';
                                                            navigate(accountSettingsPath);
                                                        }}
                                                    >
                                                        Account settings
                                                    </button>
                                                    <button
                                                        className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 transition"
                                                        onClick={() => {
                                                            setShowUserMenu(false);
                                                            window.open("https://tally.so/r/wb6krg", "_blank");
                                                        }}
                                                    >
                                                        Support
                                                    </button>
                                                    <button
                                                        className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-b-lg transition"
                                                        onClick={() => {
                                                            setShowUserMenu(false);
                                                            signOut();
                                                        }}
                                                    >
                                                        Logout
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <Link
                                            to="/login"
                                            className="px-3 py-1.5 rounded-full border border-black text-black bg-white font-medium text-sm hover:bg-gray-50 transition"
                                        >
                                            Login
                                        </Link>
                                        <Link
                                            to="/signup"
                                            className="px-3 py-1.5 rounded-full bg-black text-white font-medium text-sm hover:bg-gray-800 transition"
                                        >
                                            Sign up
                                        </Link>
                                    </>
                                )}
                            </div>

                        </div>
                    </div>

                    {/* Mobile Navigation Menu */}
                    {isMenuOpen && (
                        <div className="fixed inset-0 bg-white md:hidden z-50">
                            <div className="flex flex-col h-full">
                                {/* Header with close button */}
                                <div className="flex justify-between items-center px-6 py-4 border-b">
                                    <div className="logo cursor-pointer" onClick={() => navigate("/")}>
                                        Doctors' Jobs <span className="logo-badge">Beta</span>
                                    </div>
                                    <button
                                        className="p-2"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        <div className="w-6 h-5 flex flex-col justify-between">
                                            <span className="w-full h-0.5 bg-black transform rotate-45 translate-y-2"></span>
                                            <span className="w-full h-0.5 bg-black transform -rotate-45 -translate-y-2"></span>
                                        </div>
                                    </button>
                                </div>

                                {/* Menu Content */}
                                <div className="flex-1 overflow-y-auto p-6">
                                    <div className="flex flex-col space-y-6">
                                        {user ? (
                                            <>
                                                <div className="space-y-4">
                                                    <Link
                                                        to="/"
                                                        className={`block text-xl font-medium text-black hover:underline decoration-2 ${location.pathname === "/" && "underline"
                                                            }`}
                                                        onClick={() => setIsMenuOpen(false)}
                                                    >
                                                        Job Board
                                                    </Link>
                                                    <Link
                                                        to="/job-applications-tracker"
                                                        className={`block text-xl font-medium text-black hover:underline decoration-2 ${location.pathname === "/job-applications-tracker" && "underline"
                                                            }`}
                                                        onClick={() => setIsMenuOpen(false)}
                                                    >
                                                        Job Applications Tracker
                                                    </Link>
                                                    <a
                                                        href="#"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            handleCVAnalyzerClick(e);
                                                            setIsMenuOpen(false);
                                                        }}
                                                        className={`block text-xl font-medium text-black hover:underline decoration-2 ${(location.pathname === "/cv-analyser" || location.pathname === "/cv-analysis-results") && "underline"
                                                            }`}
                                                    >
                                                        CV Analyser
                                                    </a>
                                                </div>
                                                <div className="pt-4 border-t">
                                                    <button
                                                        className="px-6 py-2 rounded-full border border-black bg-black text-white font-medium text-sm hover:bg-black transition"
                                                        onClick={() => {
                                                            signOut();
                                                            setIsMenuOpen(false);
                                                        }}
                                                    >
                                                        Logout
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="space-y-4">
                                                    <Link
                                                        to="/employer-landing-page"
                                                        className="block text-xl font-medium text-black hover:underline"
                                                        onClick={() => setIsMenuOpen(false)}
                                                    >
                                                        Hire a doctor
                                                    </Link>
                                                    <Link
                                                        to="/employee-landing-page"
                                                        className="block text-xl font-medium text-black hover:underline"
                                                        onClick={() => setIsMenuOpen(false)}
                                                    >
                                                        Find a role
                                                    </Link>
                                                    <Link
                                                        to="/#mock-interview"
                                                        className="block text-xl font-medium text-black hover:underline"
                                                        onClick={(e) => {
                                                            setIsMenuOpen(false);
                                                            if (location.pathname === '/') {
                                                                e.preventDefault();
                                                                setTimeout(() => {
                                                                    const element = document.getElementById('mock-interview');
                                                                    if (element) {
                                                                        const navbarHeight = 80;
                                                                        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                                                                        const offsetPosition = elementPosition - navbarHeight;
                                                                        window.scrollTo({
                                                                            top: offsetPosition,
                                                                            behavior: 'smooth'
                                                                        });
                                                                        window.history.pushState(null, '', '#mock-interview');
                                                                    }
                                                                }, 100);
                                                            }
                                                        }}
                                                    >
                                                        Mock Interview
                                                    </Link>
                                                    <Link
                                                        to="/blogs"
                                                        className="block text-xl font-medium text-black hover:underline"
                                                        onClick={() => setIsMenuOpen(false)}
                                                    >
                                                        Blog
                                                    </Link>
                                                </div>
                                                <div className="space-y-4 pt-4">
                                                    <Link
                                                        to="/login"
                                                        className="block w-full px-6 py-3 rounded-full border border-black text-black bg-white font-medium text-base hover:bg-gray-50 transition text-center"
                                                        onClick={() => setIsMenuOpen(false)}
                                                    >
                                                        Login
                                                    </Link>
                                                    <Link
                                                        to="/signup"
                                                        className="block w-full px-6 py-3 rounded-full bg-black text-white font-medium text-base hover:bg-gray-800 transition text-center"
                                                        onClick={() => setIsMenuOpen(false)}
                                                    >
                                                        Sign up
                                                    </Link>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </nav>
            {/* Messages page is handled by routing */}
        </>
    );
};

export default Navbar;
